"use client";

import React from "react";
import { Loader, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

// Placeholder data - replace with actual data source
const hiddenGems = [{
  title: "Santorini Sunset",
  location: "Santorini, Greece",
  image: "https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&h=600&fit=crop"
}, {
  title: "Kyoto Gardens",
  location: "Kyoto, Japan",
  image: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&h=600&fit=crop"
}, {
  title: "Swiss Alps",
  location: "Switzerland",
  image: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=800&h=600&fit=crop"
}, {
  title: "Maldives Paradise",
  location: "Maldives",
  image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&h=600&fit=crop"
}];
export default function BeginYourCuration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <>
      
      
      <section id="begin-your-curation" ref={ref} className={`bg-background border-t border-[#e5e5e5] py-24 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            {/* Left Column - Content */}
            <div className="w-full lg:w-[40%] space-y-6 pt-4">
              <h2 className="text-4xl md:text-5xl font-bold text-[#013183] font-sans tracking-tight leading-tight">Begin Your Curation</h2>
              <div className="w-20 h-px bg-[#f97a00]" />
              <p className="text-lg font-sans leading-relaxed !text-[hsl(0,0%,0%)]">Your journey to the extraordinary starts here. We believe that travel should be as unique as you are. Tell us about your dream escape, your preferences, and your style, and let us craft a bespoke itinerary that transforms your vision into reality.</p>
              <p className="text-base text-[#013183]/80 font-sans italic">
                "The world is a book and those who do not travel read only one
                page."
              </p>
            </div>

            {/* Right Column - Form */}
            <div className="w-full lg:w-[60%]">
              <div className="bg-white p-2 md:p-6">
                <FormProvider formId="curation_form_home" sectionName="begin-your-curation" defaultValues={{
                full_name: "",
                email_address: "",
                phone_number: "",
                travel_preferences: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                email_address: {
                  required: true,
                  pattern: "email"
                },
                phone_number: {
                  required: false
                },
                travel_preferences: {
                  required: false
                }
              }} submitText="START PLANNING" successMessage="Thank you! We've received your details and will be in touch shortly to start planning your dream trip." errorMessage="We couldn't send your request. Please try again or contact us directly." className="w-full">
                  <CurationForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>;
}
function CurationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[#f97a00]/10 border border-[#f97a00]/20 rounded-lg text-center animate-in fade-in zoom-in duration-500">
        <h3 className="text-2xl font-bold text-[#013183] mb-2 font-sans">
          Request Received
        </h3>
        <p className="text-[#013183] font-sans">{successMessage}</p>
      </div>;
  }
  const inputClasses = "rounded-none border-0 border-b border-[#013183] bg-transparent px-0 py-2 shadow-none focus-visible:ring-0 focus-visible:border-[#f97a00] transition-colors duration-300 placeholder:text-transparent h-auto text-[#013183] text-base";
  const labelClasses = "text-[#013183] text-sm font-sans font-medium uppercase tracking-wide";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="E.g., Yael Cohen" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone_number" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Phone Number (Optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+972-5X-XXXX-XXX" {...field} className={inputClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="travel_preferences" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelClasses}>Travel Preferences</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="e.g., Luxury boutique hotels, adventure activities, dietary restrictions" className={`w-full min-h-[120px] resize-y ${inputClasses} focus:outline-none`} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-[#f97a00] hover:bg-[#013183] text-white font-sans font-bold text-lg py-6 rounded-md transition-all duration-300 shadow-md hover:shadow-lg uppercase tracking-wider">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : "Start Planning"}
        </Button>
      </div>
    </Form>;
}